import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import styled from 'foundations-theming/utils';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { Box } from 'foundations-components/transitional/components/Box';
import { BREAKPOINTS } from 'foundations-components/transitional/components/Grid';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Well, WellBigNumber, WellItem } from 'foundations-components/transitional/components/Well';
import WizardProgressSection from '../../components/Wizard/WizardProgressSection';
import ColorCirclesHeader from '../../components/ColorCirclesHeader';
import UsageTracker from '../../utils/UsageTracker';
import { useSelector } from 'react-redux';
import { WIZARD_URL_FRAGMENT } from '../../constants/Navigation';
import { useLocation } from 'quartz-routing/react-router';
import { useCurrentLearningInterest, useShowWizardLanguageToggle } from '../../features/wizard/wizardQueries';
import { HUBSPOT_SOFTWARE_INDEX } from '../../features/wizard/wizardTypes';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useWizardParams } from '../../features/wizard/wizardRouting';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ResultsColoredPanel = styled.div.withConfig({
  displayName: "WizardProgressInfoPanel__ResultsColoredPanel"
})(["background:", ";border-radius:16px;width:340px;height:484px;overflow:hidden;transition:flex 0.2s ease-in-out;@media (min-width:", "px){float:right;}@media (max-width:", "px){width:100%;flex:0 0 58px;z-index:100;border-top:", " solid ", ";margin-top:0;padding-top:0;&.expanded{flex:0 0 100%;}}"], ({
  theme
}) => theme.color['fill-surface-recessed'], BREAKPOINTS.md, BREAKPOINTS.sm, ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.color['border-core-on-fill-default']);
const WizardProgressInfoPanel = ({
  resultsCount,
  isRequesting,
  onStepClick,
  onSetExpanded,
  languageToggle
}) => {
  const location = useLocation();
  const {
    isMobile
  } = useMediaQuery();
  const {
    selectedCategory,
    selectedCategoryGroup,
    selectedTool,
    selectedToolsGroup,
    isToolsSelected: hubspotToolSelected
  } = useCurrentLearningInterest();
  const {
    isProgressPanelExpanded: expanded
  } = useSelector(state => state.wizard);
  const {
    showOnlyCerts
  } = useWizardParams();
  const category = hubspotToolSelected ? selectedTool : selectedCategory;
  const categoryGroup = hubspotToolSelected ? selectedToolsGroup : selectedCategoryGroup;
  const {
    show: showLanguageToggle
  } = useShowWizardLanguageToggle();
  const getArrowIcon = () => expanded ? /*#__PURE__*/_jsx(UpIcon, {
    color: "icon-core-default",
    size: "800"
  }) : /*#__PURE__*/_jsx(DownIcon, {
    color: "icon-core-default",
    size: "800"
  });
  const getMotivationTextLine1 = () => showOnlyCerts ? 'learningCenter.wizard.modal.motivationbuttons.advancecareer' : 'learningCenter.wizard.modal.motivationbuttons.currenttrends';
  const renderHeaderTitle = () => /*#__PURE__*/_jsx(Text, {
    as: "h6",
    color: "text-core-default",
    marginBlockEnd: "0",
    textTransform: "uppercase",
    typography: "heading-50",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.wizard.progresspanel.courses"
    })
  });
  const getHeader = () => /*#__PURE__*/_jsx(View, {
    borderBlockEndColor: "border-core-default",
    borderBlockEndStyle: "solid",
    borderBlockEndWidth: "100",
    marginBlockEnd: "400",
    paddingBlockEnd: "100",
    children: /*#__PURE__*/_jsx(Well, {
      children: /*#__PURE__*/_jsxs(WellItem, {
        marginBlockEnd: "600",
        marginBlockStart: "0",
        children: [renderHeaderTitle(), /*#__PURE__*/_jsx(WellBigNumber, {
          "data-test-id": "wizard-results-count",
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(View, {
            blockSize: "1000",
            overflowY: "hidden",
            children: isRequesting ? /*#__PURE__*/_jsx(LoadingSpinner, {
              size: "sm"
            }) : /*#__PURE__*/_jsx(Text, {
              color: "text-core-default",
              typography: "heading-900",
              children: I18n.formatNumber(resultsCount)
            })
          })
        }), languageToggle]
      })
    })
  });
  const onClickExpanded = () => {
    UsageTracker.track('wizardInteraction', {
      action: 'wizard-expand-results-section-mobile'
    });
    onSetExpanded(!expanded);
  };
  const onSectionClick = step => {
    UsageTracker.track('wizardInteraction', {
      action: 'wizard-click-green-results-section',
      stepClicked: step
    });
    onStepClick();
  };
  const getMobileHeader = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        borderBlockEndColor: "border-core-subtle",
        borderBlockEndStyle: "solid",
        borderBlockEndWidth: "100",
        children: /*#__PURE__*/_jsx(Well, {
          padding: "0",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsxs(Flex, {
              alignItems: "end",
              marginBlockEnd: "200",
              children: [/*#__PURE__*/_jsx(Box, {
                marginInlineEnd: "200",
                children: /*#__PURE__*/_jsx(View, {
                  marginBlockStart: "100",
                  minBlockSize: "48px",
                  overflowY: "hidden",
                  children: isRequesting ? /*#__PURE__*/_jsx(LoadingSpinner, {
                    size: "sm"
                  }) : /*#__PURE__*/_jsx(Text, {
                    color: "text-core-default",
                    typography: "heading-800",
                    children: I18n.formatNumber(resultsCount)
                  })
                })
              }), /*#__PURE__*/_jsx(Box, {
                grow: 1,
                marginBlockEnd: "200",
                children: renderHeaderTitle()
              })]
            }), /*#__PURE__*/_jsx(Box, {
              children: /*#__PURE__*/_jsx(IconButton, {
                "data-test-id": "wizard-progress-header-expand",
                onClick: onClickExpanded,
                use: "transparent",
                children: getArrowIcon()
              })
            })]
          })
        })
      }), /*#__PURE__*/_jsx(Box, {
        paddingBlock: "300",
        children: languageToggle
      })]
    });
  };
  const getProgressPanelHeader = () => {
    return isMobile ? getMobileHeader() : getHeader();
  };
  const getSectionUrl = step => {
    let url = WIZARD_URL_FRAGMENT;
    if (hubspotToolSelected) url += `/${HUBSPOT_SOFTWARE_INDEX}`;
    const {
      search
    } = location;
    switch (step) {
      case 'motivation':
        return `${[url, categoryGroup].filter(e => e).join('/')}${search}`;
      case 'final':
        return `${[url, categoryGroup, category].filter(e => e).join('/')}${search}`;
      default:
        return `${url}${search}`;
    }
  };
  const renderSectionStepCategory = () => {
    return /*#__PURE__*/_jsx(WizardProgressSection, {
      "data-test-id": "wizard-progress-section-1",
      link: getSectionUrl('category'),
      linkMessageLine1: `learningCenter.filtersGroups.${categoryGroup}`,
      noteMessage: "learningCenter.wizard.progresspanel.categorygrouptitle",
      onProgressStepClick: () => {
        onSectionClick('category');
      }
    });
  };
  const renderSectionStepMotivation = () => {
    return /*#__PURE__*/_jsx(WizardProgressSection, {
      "data-test-id": "wizard-progress-section-2",
      link: getSectionUrl('motivation'),
      linkMessageLine1: hubspotToolSelected ? `learningCenter.topFilters.tools.${category}` : `learningCenter.topFilters.cat.${category}`,
      noteMessage: "learningCenter.wizard.progresspanel.categorytitle",
      noteOptions: {
        categoryGroup: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `learningCenter.filtersGroups.${categoryGroup}`
        })
      },
      onProgressStepClick: () => {
        onSectionClick('motivation');
      }
    });
  };
  const renderSectionStepFinal = () => {
    return /*#__PURE__*/_jsx(WizardProgressSection, {
      "data-test-id": "wizard-progress-section-3",
      link: getSectionUrl('final'),
      linkMessageLine1: getMotivationTextLine1(),
      noteMessage: "learningCenter.wizard.progresspanel.motivationtitle",
      onProgressStepClick: () => {
        onSectionClick('final');
      }
    });
  };
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(ResultsColoredPanel, {
      $showLanguageToggle: showLanguageToggle,
      className: expanded ? 'expanded' : '',
      children: /*#__PURE__*/_jsx(ColorCirclesHeader, {
        backgroundColor: "transparent",
        containerHeight: "100%",
        containerWidth: "100%",
        height: "150%",
        left: "-90px",
        padding: "0",
        top: "-110px",
        transform: "rotate(45deg)",
        width: "150%",
        children: /*#__PURE__*/_jsxs(View, {
          paddingInline: isMobile ? '200' : '700',
          children: [/*#__PURE__*/_jsx(TFlex, {
            direction: "column"
          }), getProgressPanelHeader(), categoryGroup && renderSectionStepCategory(), category && renderSectionStepMotivation(), showOnlyCerts !== undefined && renderSectionStepFinal()]
        })
      })
    })
  );
};
export default WizardProgressInfoPanel;